import { ContentApiError } from '3-foundation/Error/contentApiError';
import { axiosInstance as axios } from '3-foundation/ContentApi/axiosInstance';
import { CONTENT_API_URL } from '../_constants';
import { requestHeaders } from './requestHeaders';
import Application from '3-foundation/Application/ApplicationEntity';
import logger from '3-foundation/Logging/logger';
import { AxiosError } from '3-foundation/Error/axiosError';

export interface RequestInterface {
	preview?: boolean;
	previewData?: Content.PreviewData;
	path?: string;
	query?: string;
	method: 'GET' | 'POST';
}

export const queryContentApi = async <TResponse = unknown>({ preview, previewData, method, query, path }: RequestInterface): Promise<TResponse> => {
	if (!CONTENT_API_URL) {
		logger.error('[500] Internal Server Error \nContent api url is not defined');
		throw new Error('Internal Server Error 500', { cause: 'Content api is not defined' });
	}

	const origin = Application.url?.origin;
	const accessToken = Application.session?.accessToken;
	const locale = Application.locale?.baseName;
	const hostname = `${new URL(origin).hostname}/${locale}`;

	// We are using separate sites for language handling,
	// so we don't send the locale to avoid triggering a content culture header
	// instead we put a content language header on the request that the API can use
	const headers = requestHeaders({ hostname, preview, previewData, language: locale, accessToken });

	const contenturl = encodeURI(`${CONTENT_API_URL}${path ? path : ''}${preview ? '?preview=1' : ''}`);
	const requestOptions = {
		url: contenturl,
		method: method,
		maxRedirects: 0,
		headers,
		data: {
			query,
		},
	};

	try {
		const response = await axios(requestOptions);
		const { data, errors } = response?.data ?? {};
		let result;
		if (method === 'POST') {
			if (errors) throw errors;
			if (!data) throw new Error(`The Content Api returned no content from '${contenturl}'. Is the content system configured and running?`);
			result = data;
			result.preview = preview ? true : false;
			result.previewData = previewData ? previewData : null;
		}

		if (method === 'GET') {
			result = (data || response?.data) ?? {};
		}
		return result;
	} catch (error) {
		if (axios.isAxiosError(error)) throw new AxiosError(error, { cause: error });

		const message = error?.response?.data?.message ?? error;
		const statusCode = error?.response?.status || 500;

		if (statusCode === 301 || statusCode === 302 || statusCode === 307 || statusCode === 308) {
			const statusName = statusCode
				? statusCode === 301
					? 'Moved Permanently'
					: statusCode === 302
						? 'Found'
						: statusCode === 307
							? 'Temporary Redirect'
							: statusCode === 308
								? 'Permanent Redirect'
								: ''
				: 'statusCode not defined';
			logger.info(`[${statusCode}] Redirection: ${statusName} \n${error?.response?.headers?.location}`);
			throw new ContentApiError({ name: 'Redirection', query, error, cause: error });
		}

		if (statusCode === 404) {
			logger.error(`[${statusCode}] Client error \nPage not found`, { path: path });
			throw new ContentApiError({ name: 'PageNotFound', query, error, cause: error });
		}

		if (statusCode === 401 || statusCode === 403) {
			// "401 Unauthorized" really means "unauthenticated"
			// "403 Forbidden" really means "unauthorized"
			logger.error(`[${statusCode}] Client error \n${error?.response?.data?.data}`);
			throw new ContentApiError({ name: 'Auth error', query, error: error?.response?.data?.data, cause: error });
		}

		logger.error(`[${statusCode}] Undetected API error in queryContentApi() \n${message}`);
		throw new ContentApiError({ name: 'Undetected API error', query, error, cause: error });
	}
};
